import { useEffect, useState } from "react";
import { isEqual } from "date-fns";
import { toDate } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { formatAPIDate } from "shared/api/utils";
import { DataType } from "shared/types";

import DatePicker from "features/ui/DatePicker";
import DatePickerRange from "features/ui/DatePickerRange";
import DropdownWithSearch from "features/ui/DropdownWithSearch/DropdownWithSearch";
import InputFilter from "features/ui/Filters/FilterTypes/InputFilter";
import { FilterOperator, InitialDateValues } from "features/ui/Filters/types";
import {
  DateOption,
  getDateUnitOptions,
  getDefaultDateUnitOption,
  getDefaultLastXValue,
  isDateTimeField,
  isValidDate,
  isValueInLastXFormat,
} from "features/ui/Filters/utils";
import { SelectOption } from "features/ui/Select";

interface Props {
  fieldName: string;
  selectedOperatorId: FilterOperator;
  initialValues?: InitialDateValues;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  defaultIsOpen?: boolean;
  disabled?: boolean;
  dataType?: DataType;
  autoFocus?: boolean;
  isInsideFilterBuilder?: boolean;
}

const DateFilters = ({
  fieldName,
  selectedOperatorId,
  initialValues,
  onDateFromChange,
  onDateToChange,
  defaultIsOpen,
  disabled,
  dataType,
  autoFocus,
  isInsideFilterBuilder = false,
}: Props) => {
  const { t } = useTranslation("filters");
  const isDateTime = isDateTimeField(dataType);

  const [lastXValue, setLastXValue] = useState(
    getDefaultLastXValue(initialValues)
  );
  const [selectedUnitOption, setSelectedUnitOption] = useState(
    getDefaultDateUnitOption(t, initialValues)
  );

  const initialDateStart =
    (initialValues &&
      initialValues[0] &&
      initialValues[0] !== "null" &&
      !isValueInLastXFormat(initialValues[0]) &&
      toDate(initialValues[0])) ||
    null;

  const initialDateEnd =
    (initialValues &&
      initialValues[1] &&
      !isValueInLastXFormat(initialValues[1]) &&
      toDate(initialValues[1])) ||
    null;

  const handleDateChange = (
    date: Date | null,
    callback: (value: string) => void
  ) => {
    // Only format and trigger change if we have a valid date with a reasonable year
    if (isValidDate(date)) {
      callback(formatAPIDate(date!.toString(), dataType));
    }
  };

  const handleInLastFilter = (
    lastXValue: string,
    selectedUnitOption: SelectOption,
    selectedOperatorId: FilterOperator
  ) => {
    if (
      !lastXValue ||
      !selectedUnitOption ||
      selectedOperatorId !== FilterOperator.IN_LAST
    ) {
      return;
    }

    onDateFromChange(`${lastXValue}${selectedUnitOption.id}`);
  };

  const handleLastXChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLastXValue(value);
    handleInLastFilter(value, selectedUnitOption, selectedOperatorId);
  };

  const handleUnitChange = (selectedOption: SelectOption<DateOption>) => {
    setSelectedUnitOption(selectedOption);
    handleInLastFilter(lastXValue, selectedOption, selectedOperatorId);
  };

  useEffect(() => {
    if (selectedOperatorId !== FilterOperator.IN_LAST || initialValues) {
      return;
    }

    // We decide on default values for last X filter so we need to sync them with filter state even before
    // we let user update the filters first
    handleInLastFilter(lastXValue, selectedUnitOption, selectedOperatorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (selectedOperatorId === FilterOperator.BETWEEN) {
    return (
      <DatePickerRange
        key={fieldName}
        initialDateStart={initialDateStart}
        initialDateEnd={initialDateEnd}
        onChange={(dateFrom: Date | null, dateTo: Date | null) => {
          if (
            dateFrom &&
            (!initialDateStart || !isEqual(dateFrom, initialDateStart))
          ) {
            handleDateChange(dateFrom, onDateFromChange);
          }

          if (dateTo && (!initialDateEnd || !isEqual(dateTo, initialDateEnd))) {
            handleDateChange(dateTo, onDateToChange);
          }
        }}
        defaultIsOpen={defaultIsOpen}
        disabled={disabled}
        showTimeSelect={isDateTime}
        autoFocus={autoFocus}
      />
    );
  }

  if (selectedOperatorId === FilterOperator.IN_LAST) {
    return (
      <div className="flex flex-row items-center gap-2">
        <InputFilter
          autoFocus={autoFocus}
          fieldName={fieldName}
          type="number"
          value={lastXValue}
          onChange={handleLastXChange}
          slotProps={{ htmlInput: { min: 1 } }}
          disabled={disabled}
        />
        <DropdownWithSearch
          options={getDateUnitOptions(t)}
          selectedOption={selectedUnitOption}
          onSelectedOptionChange={handleUnitChange}
          disabled={disabled}
          disableClearable={true}
          autoWidth={false}
          width={120}
        />
      </div>
    );
  }

  if (
    [
      FilterOperator.GREATER_OR_EQUAL,
      FilterOperator.GREATER_THAN,
      FilterOperator.LESS_OR_EQUAL,
      FilterOperator.LESS_THAN,
    ].includes(selectedOperatorId)
  ) {
    return (
      <DatePicker
        autoFocus={autoFocus}
        initialDate={initialDateStart}
        disabled={disabled}
        showTimeSelect={isDateTime}
        onChange={(date: Date | null) => {
          handleDateChange(date, onDateFromChange);
        }}
        isInsideFilterBuilder={isInsideFilterBuilder}
      />
    );
  }

  return null;
};

export default DateFilters;
